<template>
  <div class="account-page">
    <v-container class="account-container pa-0" fluid>
      <v-card class="account-shell" elevation="0">
        <div class="account-header">
          <div>
            <h2 class="header-title">Account Settings</h2>
            <p class="header-subtitle">Manage your profile photo and personal details.</p>
          </div>
          <v-chip color="primary" small outlined class="header-chip">
            Secure Profile
          </v-chip>
        </div>

        <v-row class="ma-0">
          <v-col cols="12" md="4" class="pa-3 pa-md-4">
            <v-card class="section-card photo-card" outlined>
              <h3 class="section-title">Profile Photo</h3>

              <div class="avatar-wrap">
                <v-avatar size="132" class="profile-avatar">
                  <v-img :src="previewImage" />
                </v-avatar>
              </div>

              <div class="upload-box" @click="$refs.file.click()">
                <input
                  id="file"
                  ref="file"
                  type="file"
                  class="inputfile"
                  accept=".jpg,.png,.jpeg"
                  @change="fileInput"
                />
                <v-icon color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
                <div class="upload-title">Upload photo</div>
                <div class="upload-hint">{{ inputFileName }}</div>
              </div>

              <v-btn
                block
                color="primary"
                class="mt-4 action-btn"
                :loading="loading"
                @click="updateAccountPic"
              >
                Update Photo
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="8" class="pa-3 pa-md-4">
            <v-card class="section-card info-card" outlined>
              <h3 class="section-title">Personal Information</h3>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="fullnames"
                    ref="fullnames"
                    v-model="fullnames"
                    label="Full Names"
                    outlined
                    dense
                    prepend-inner-icon="mdi-account-outline"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Your Full Names is required']"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    id="username"
                    ref="username"
                    v-model="username"
                    label="Username"
                    outlined
                    dense
                    prepend-inner-icon="mdi-at"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Username is required']"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <div class="phone-modern">
                    <v-icon color="primary" class="phone-icon">mdi-phone-outline</v-icon>
                    <input
                      id="phone"
                      ref="phone_number"
                      v-model="phone_number"
                      type="tel"
                      required
                      name="phone"
                      class="phone-input"
                      placeholder="Enter phone number"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    id="email"
                    ref="email"
                    v-model="email"
                    label="Email Address"
                    outlined
                    dense
                    prepend-inner-icon="mdi-email-outline"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Your email is required']"
                  />
                </v-col>
              </v-row>

              <div class="save-row">
                <v-btn
                  color="primary"
                  large
                  class="action-btn save-btn"
                  :disabled="isDisabled || loading"
                  :loading="loading"
                  @click="confirmDialog = true"
                >
                  Save Changes
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <v-dialog v-model="confirmDialog" max-width="420" persistent>
      <v-card class="confirm-card" rounded="lg">
        <v-card-title class="confirm-title">
          Confirm changes
        </v-card-title>
        <v-card-text class="pt-4">
          Do you want to save these profile updates now?
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer />
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitAccountUpdate">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      loading: false,
      isDisabled: false,
      confirmDialog: false,
      inputFileName: 'Allowed JPG or PNG. Max size of 5Mbytes',
      file: null,
      username: '',
      fullnames: '',
      phone_number: '',
      phoneInput: null,
      email: '',
      get_image: ''
    }
  },

  computed: {
    previewImage() {
      return this.get_image || 'https://via.placeholder.com/132x132.png?text=User'
    }
  },

  mounted() {
    this.initialiseIntel()
    this.getUserInfo()
    this.$store.commit('setCloseURL', '/')
  },

  methods: {
    initialiseIntel() {
      const getIp = callback => {
        fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { headers: { Accept: 'application/json' } })
          .then(resp => resp.json())
          .catch(() => ({ country: 'cm' }))
          .then(resp => callback(resp.country))
      }

      const phoneInputField = document.querySelector('#phone')
      if (!phoneInputField || !window.intlTelInput) return

      this.phoneInput = window.intlTelInput(phoneInputField, {
        initialCountry: 'auto',
        geoIpLookup: getIp,
        preferredCountries: ['cm', 'us', 'uk', 'ci', 'cg', 'cf', 'td', 'ng', 'gq', 'ga'],
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js'
      })
    },

    submitAccountUpdate() {
      this.confirmDialog = false
      this.updateAccountDetails()
    },

    async updateAccountDetails() {
      if (this.username.length < 4) {
        this.$store.commit('setSnackBarMessage', 'User name is too short, user name needs to be at least 4 characters')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.username.focus()
        return
      }

      if (!this.fullnames) {
        this.$store.commit('setSnackBarMessage', 'Please input full names')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.fullnames.focus()
        return
      }

      if (this.phoneInput && !this.phoneInput.isValidNumber()) {
        this.$store.commit('setSnackBarMessage', 'Phone number is invalid. Please input a valid number')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.phone_number.focus()
        return
      }

      if (!this.email) {
        this.$store.commit('setSnackBarMessage', 'Please input email')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.email.focus()
        return
      }

      if (this.file && this.file.size > 6000000) {
        this.$store.commit('setSnackBarMessage', 'Please upload a picture less than 5mb')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.loading = true
      this.isDisabled = true

      const formData = new FormData()
      formData.append('username', this.username)
      formData.append('fullnames', this.fullnames)
      formData.append('phone_number', this.phoneInput ? this.phoneInput.getNumber() : this.phone_number)
      formData.append('email', this.email)

      if (this.file) {
        formData.append('user_pic', this.file)
      }

      try {
        const response = await axios.post('/api/v1/home/update_account_details/account/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor', response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)

        if (response.data[0].category === 'success') {
          this.getUserInfo()
        }
      } catch (error) {
        if (error.response) {
          Object.keys(error.response.data).forEach(property => {
            this.$store.commit('setSnackBarMessage', error.response.data[property][0])
            this.$store.commit('setSnackBarColor', 'red darken-4')
            this.$store.commit('activateSnackBar', true)
          })
        } else {
          this.$store.commit('setSnackBarMessage', 'An error occured while updating your account')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        }
      } finally {
        this.loading = false
        this.isDisabled = false
      }
    },

    async updateAccountPic() {
      if (!this.file) {
        this.$store.commit('setSnackBarMessage', 'Please upload your profile picture')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.loading = true
      const formData = new FormData()
      formData.append('user_pic', this.file)

      try {
        const response = await axios.post('/api/v1/home/update_account_details/user_pic/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor', response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)

        if (response.data[0].category === 'success') {
          this.getUserInfo()
          this.file = null
          this.inputFileName = 'Allowed JPG or PNG. Max size of 5Mbytes'
        }
      } catch (error) {
        if (error.response) {
          Object.keys(error.response.data).forEach(property => {
            this.$store.commit('setSnackBarMessage', error.response.data[property][0])
            this.$store.commit('setSnackBarColor', 'red darken-4')
            this.$store.commit('activateSnackBar', true)
          })
        } else {
          this.$store.commit('setSnackBarMessage', 'An error occured while updating your profile picture')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        }
      } finally {
        this.loading = false
      }
    },

    getUserInfo() {
  if (!this.$store.state.isAuthenticated) return

  axios
    .get('/api/v1/home/get/user/info/')
    .then(response => {
      const data = response.data[0]

      // 1. Mise à jour du store Vuex
      this.$store.commit('setUser', data.user)
      this.$store.commit('setUserState', data.user_info)
      this.$store.commit('setUserInfo', data)
      this.$store.commit('setUserName', data.user.username)

      // 2. Reconstitution du nom complet (First Name + Last Name)
      const firstName = data.user.first_name || ''
      const lastName = data.user.last_name || ''
      this.fullnames = `${firstName} ${lastName}`.trim() || data.user.username

      // 3. Affectation des autres champs
      this.username = data.user.username
      this.email = data.user.email
      this.phone_number = data.user_profile.phone_number
      this.get_image = data.user_profile.get_image

      // 4. Si vous avez un champ CNI dans ce formulaire :
      if (this.userForm) {
        this.userForm.idCardNumber = data.user_profile.id_card_number || ''
      }
    })
    .catch(() => {
      this.$store.commit('setSnackBarMessage', 'An error occured, please check your internet connection.')
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    })
},

    fileInput() {
      const input = document.getElementById('file')
      if (!input || !input.files.item(0)) {
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      let fileName = input.files.item(0).name
      const fileSize = input.files.item(0).size
      const fileType = input.files.item(0).type
      fileName = fileName.replace(/.*[\\/]/, '_')

      if (fileSize >= 5242880) {
        this.$store.commit('setSnackBarMessage', 'Your profile picture exceeded the size limit of 5Mb. Upload a file less than 5 megabytes')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png')) {
        this.$store.commit('setSnackBarMessage', 'Your profile picture must be an image (.png, .jpeg, .jpg)')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      if (fileName.length > 22) {
        fileName = `${fileName.substring(0, 9)}...${fileName.substring(fileName.length - 10)}`
      }

      this.inputFileName = fileName
      this.file = this.$refs.file.files[0]
    }
  }
}
</script>

<style scoped>
.account-page {
  padding: 16px 8px 8px;
}

.account-container {
  max-width: 1100px;
  margin: 0 auto;
}

.account-shell {
  border-radius: 22px;
  background: linear-gradient(145deg, #f7faff 0%, #ffffff 45%, #f9fbff 100%);
  border: 1px solid rgba(23, 84, 180, 0.1);
  box-shadow: 0 16px 50px rgba(17, 63, 140, 0.08);
  overflow: hidden;
  animation: fadeInUp 0.45s ease;
}

.account-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 24px 26px;
  border-bottom: 1px solid rgba(23, 84, 180, 0.1);
  background: linear-gradient(120deg, rgba(25, 118, 210, 0.06) 0%, rgba(111, 154, 255, 0.04) 100%);
}

.header-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #103d8c;
  letter-spacing: 0.2px;
}

.header-subtitle {
  margin: 6px 0 0;
  color: #5f6b7d;
  font-size: 0.94rem;
}

.header-chip {
  font-weight: 600;
}

.section-card {
  border-radius: 16px;
  border-color: rgba(16, 61, 140, 0.14) !important;
  background: #fff;
  padding: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(18, 67, 149, 0.12);
}

.section-title {
  margin: 2px 0 16px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a3f78;
}

.avatar-wrap {
  display: flex;
  justify-content: center;
  margin: 8px 0 16px;
}

.profile-avatar {
  border: 4px solid #fff;
  box-shadow: 0 10px 26px rgba(17, 57, 121, 0.18);
}

.upload-box {
  border: 1px dashed rgba(25, 118, 210, 0.45);
  border-radius: 14px;
  padding: 18px 12px;
  text-align: center;
  background: rgba(25, 118, 210, 0.03);
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-box:hover {
  border-color: rgba(25, 118, 210, 0.85);
  background: rgba(25, 118, 210, 0.08);
}

.upload-title {
  color: #1d4b96;
  font-weight: 600;
  font-size: 0.95rem;
}

.upload-hint {
  margin-top: 4px;
  color: #6b7789;
  font-size: 0.8rem;
  word-break: break-word;
}

.field-modern :deep(.v-input__slot) {
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.field-modern :deep(.v-input__slot:hover) {
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.08);
}

.phone-modern {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(16, 61, 140, 0.22);
  border-radius: 12px;
  padding: 11px 12px;
  min-height: 56px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.phone-modern:focus-within {
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.14);
}

.phone-icon {
  flex: 0 0 auto;
}

.phone-input {
  width: 100%;
  outline: none;
  border: 0;
  font-size: 0.95rem;
  background: transparent;
  color: #1c2636;
}

.save-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-radius: 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.22);
}

.save-btn {
  min-width: 170px;
}

.confirm-card {
  border-radius: 16px;
}

.confirm-title {
  background: linear-gradient(120deg, #1e6fd0 0%, #3f8df0 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
}

.inputfile {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .account-header {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .account-page {
    padding: 8px 0 0;
  }

  .account-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .header-title {
    font-size: 1.2rem;
  }

  .section-card {
    padding: 14px;
  }

  .save-row {
    justify-content: stretch;
  }

  .save-btn {
    width: 100%;
  }
}
</style>
<template>
  <div class="account-page">
    <v-container class="account-container pa-0" fluid>
      <v-card class="account-shell" elevation="0">
        <div class="account-header">
          <div>
            <h2 class="header-title">Account Settings</h2>
            <p class="header-subtitle">Manage your profile photo and personal details.</p>
          </div>
          <v-chip color="primary" small outlined class="header-chip">
            Secure Profile
          </v-chip>
        </div>

        <v-row class="ma-0">
          <v-col cols="12" md="4" class="pa-3 pa-md-4">
            <v-card class="section-card photo-card" outlined>
              <h3 class="section-title">Profile Photo</h3>

              <div class="avatar-wrap">
                <v-avatar size="132" class="profile-avatar">
                  <v-img :src="previewImage" />
                </v-avatar>
              </div>

              <div class="upload-box" @click="$refs.file.click()">
                <input
                  id="file"
                  ref="file"
                  type="file"
                  class="inputfile"
                  accept=".jpg,.png,.jpeg"
                  @change="fileInput"
                />
                <v-icon color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
                <div class="upload-title">Upload photo</div>
                <div class="upload-hint">{{ inputFileName }}</div>
              </div>

              <v-btn
                block
                color="primary"
                class="mt-4 action-btn"
                :loading="loading"
                @click="updateAccountPic"
              >
                Update Photo
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="8" class="pa-3 pa-md-4">
            <v-card class="section-card info-card" outlined>
              <h3 class="section-title">Personal Information</h3>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    id="fullnames"
                    ref="fullnames"
                    v-model="fullnames"
                    label="Full Names"
                    outlined
                    dense
                    prepend-inner-icon="mdi-account-outline"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Your Full Names is required']"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    id="username"
                    ref="username"
                    v-model="username"
                    label="Username"
                    outlined
                    dense
                    prepend-inner-icon="mdi-at"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Username is required']"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <div class="phone-modern">
                    <v-icon color="primary" class="phone-icon">mdi-phone-outline</v-icon>
                    <input
                      id="phone"
                      ref="phone_number"
                      v-model="phone_number"
                      type="tel"
                      required
                      name="phone"
                      class="phone-input"
                      placeholder="Enter phone number"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    id="email"
                    ref="email"
                    v-model="email"
                    label="Email Address"
                    outlined
                    dense
                    prepend-inner-icon="mdi-email-outline"
                    color="primary"
                    class="field-modern"
                    :rules="[v => !!v || 'Your email is required']"
                  />
                </v-col>
              </v-row>

              <div class="save-row">
                <v-btn
                  color="primary"
                  large
                  class="action-btn save-btn"
                  :disabled="isDisabled || loading"
                  :loading="loading"
                  @click="confirmDialog = true"
                >
                  Save Changes
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <v-dialog v-model="confirmDialog" max-width="420" persistent>
      <v-card class="confirm-card" rounded="lg">
        <v-card-title class="confirm-title">
          Confirm changes
        </v-card-title>
        <v-card-text class="pt-4">
          Do you want to save these profile updates now?
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer />
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitAccountUpdate">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      loading: false,
      isDisabled: false,
      confirmDialog: false,
      inputFileName: 'Allowed JPG or PNG. Max size of 5Mbytes',
      file: null,
      username: '',
      fullnames: '',
      phone_number: '',
      phoneInput: null,
      email: '',
      get_image: ''
    }
  },

  computed: {
    previewImage() {
      return this.get_image || 'https://via.placeholder.com/132x132.png?text=User'
    }
  },

  mounted() {
    this.initialiseIntel()
    this.getUserInfo()
    this.$store.commit('setCloseURL', '/')
  },

  methods: {
    initialiseIntel() {
      const getIp = callback => {
        fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { headers: { Accept: 'application/json' } })
          .then(resp => resp.json())
          .catch(() => ({ country: 'cm' }))
          .then(resp => callback(resp.country))
      }

      const phoneInputField = document.querySelector('#phone')
      if (!phoneInputField || !window.intlTelInput) return

      this.phoneInput = window.intlTelInput(phoneInputField, {
        initialCountry: 'auto',
        geoIpLookup: getIp,
        preferredCountries: ['cm', 'us', 'uk', 'ci', 'cg', 'cf', 'td', 'ng', 'gq', 'ga'],
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js'
      })
    },

    submitAccountUpdate() {
      this.confirmDialog = false
      this.updateAccountDetails()
    },

    async updateAccountDetails() {
      if (this.username.length < 4) {
        this.$store.commit('setSnackBarMessage', 'User name is too short, user name needs to be at least 4 characters')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.username.focus()
        return
      }

      if (!this.fullnames) {
        this.$store.commit('setSnackBarMessage', 'Please input full names')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.fullnames.focus()
        return
      }

      if (this.phoneInput && !this.phoneInput.isValidNumber()) {
        this.$store.commit('setSnackBarMessage', 'Phone number is invalid. Please input a valid number')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.phone_number.focus()
        return
      }

      if (!this.email) {
        this.$store.commit('setSnackBarMessage', 'Please input email')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.$refs.email.focus()
        return
      }

      if (this.file && this.file.size > 6000000) {
        this.$store.commit('setSnackBarMessage', 'Please upload a picture less than 5mb')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.loading = true
      this.isDisabled = true

      const formData = new FormData()
      formData.append('username', this.username)
      formData.append('fullnames', this.fullnames)
      formData.append('phone_number', this.phoneInput ? this.phoneInput.getNumber() : this.phone_number)
      formData.append('email', this.email)

      if (this.file) {
        formData.append('user_pic', this.file)
      }

      try {
        const response = await axios.post('/api/v1/home/update_account_details/account/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor', response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)

        if (response.data[0].category === 'success') {
          this.getUserInfo()
        }
      } catch (error) {
        if (error.response) {
          Object.keys(error.response.data).forEach(property => {
            this.$store.commit('setSnackBarMessage', error.response.data[property][0])
            this.$store.commit('setSnackBarColor', 'red darken-4')
            this.$store.commit('activateSnackBar', true)
          })
        } else {
          this.$store.commit('setSnackBarMessage', 'An error occured while updating your account')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        }
      } finally {
        this.loading = false
        this.isDisabled = false
      }
    },

    async updateAccountPic() {
      if (!this.file) {
        this.$store.commit('setSnackBarMessage', 'Please upload your profile picture')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.loading = true
      const formData = new FormData()
      formData.append('user_pic', this.file)

      try {
        const response = await axios.post('/api/v1/home/update_account_details/user_pic/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor', response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)

        if (response.data[0].category === 'success') {
          this.getUserInfo()
          this.file = null
          this.inputFileName = 'Allowed JPG or PNG. Max size of 5Mbytes'
        }
      } catch (error) {
        if (error.response) {
          Object.keys(error.response.data).forEach(property => {
            this.$store.commit('setSnackBarMessage', error.response.data[property][0])
            this.$store.commit('setSnackBarColor', 'red darken-4')
            this.$store.commit('activateSnackBar', true)
          })
        } else {
          this.$store.commit('setSnackBarMessage', 'An error occured while updating your profile picture')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        }
      } finally {
        this.loading = false
      }
    },

    getUserInfo() {
      if (!this.$store.state.isAuthenticated) return

      axios
        .get('/api/v1/home/get/user/info/')
        .then(response => {
          this.$store.commit('setUser', response.data[0].user)
          this.$store.commit('setUserState', response.data[0].user_info)
          this.$store.commit('setUserInfo', response.data[0])
          this.$store.commit('setUserName', response.data[0].user.username)

          this.username = this.$store.state.user_info.user.username
          this.fullnames = this.$store.state.user_info.user.first_name
          this.phone_number = this.$store.state.user_info.user_profile.phone_number
          this.email = this.$store.state.user_info.user.email
          this.get_image = this.$store.state.user_info.user_profile.get_image
        })
        .catch(() => {
          this.$store.commit('setSnackBarMessage', 'An error occured, please check your internet connection and try again later.')
          this.$store.commit('setSnackBarColor', 'red darken-4')
          this.$store.commit('activateSnackBar', true)
        })
    },

    fileInput() {
      const input = document.getElementById('file')
      if (!input || !input.files.item(0)) {
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      let fileName = input.files.item(0).name
      const fileSize = input.files.item(0).size
      const fileType = input.files.item(0).type
      fileName = fileName.replace(/.*[\\/]/, '_')

      if (fileSize >= 5242880) {
        this.$store.commit('setSnackBarMessage', 'Your profile picture exceeded the size limit of 5Mb. Upload a file less than 5 megabytes')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png')) {
        this.$store.commit('setSnackBarMessage', 'Your profile picture must be an image (.png, .jpeg, .jpg)')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        this.inputFileName = 'Select Profile Picture'
        this.file = null
        return
      }

      if (fileName.length > 22) {
        fileName = `${fileName.substring(0, 9)}...${fileName.substring(fileName.length - 10)}`
      }

      this.inputFileName = fileName
      this.file = this.$refs.file.files[0]
    }
  }
}
</script>

<style scoped>
.account-page {
  padding: 16px 8px 8px;
}

.account-container {
  max-width: 1100px;
  margin: 0 auto;
}

.account-shell {
  border-radius: 22px;
  background: linear-gradient(145deg, #f7faff 0%, #ffffff 45%, #f9fbff 100%);
  border: 1px solid rgba(23, 84, 180, 0.1);
  box-shadow: 0 16px 50px rgba(17, 63, 140, 0.08);
  overflow: hidden;
  animation: fadeInUp 0.45s ease;
}

.account-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 24px 26px;
  border-bottom: 1px solid rgba(23, 84, 180, 0.1);
  background: linear-gradient(120deg, rgba(25, 118, 210, 0.06) 0%, rgba(111, 154, 255, 0.04) 100%);
}

.header-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #103d8c;
  letter-spacing: 0.2px;
}

.header-subtitle {
  margin: 6px 0 0;
  color: #5f6b7d;
  font-size: 0.94rem;
}

.header-chip {
  font-weight: 600;
}

.section-card {
  border-radius: 16px;
  border-color: rgba(16, 61, 140, 0.14) !important;
  background: #fff;
  padding: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(18, 67, 149, 0.12);
}

.section-title {
  margin: 2px 0 16px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a3f78;
}

.avatar-wrap {
  display: flex;
  justify-content: center;
  margin: 8px 0 16px;
}

.profile-avatar {
  border: 4px solid #fff;
  box-shadow: 0 10px 26px rgba(17, 57, 121, 0.18);
}

.upload-box {
  border: 1px dashed rgba(25, 118, 210, 0.45);
  border-radius: 14px;
  padding: 18px 12px;
  text-align: center;
  background: rgba(25, 118, 210, 0.03);
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-box:hover {
  border-color: rgba(25, 118, 210, 0.85);
  background: rgba(25, 118, 210, 0.08);
}

.upload-title {
  color: #1d4b96;
  font-weight: 600;
  font-size: 0.95rem;
}

.upload-hint {
  margin-top: 4px;
  color: #6b7789;
  font-size: 0.8rem;
  word-break: break-word;
}

.field-modern :deep(.v-input__slot) {
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.field-modern :deep(.v-input__slot:hover) {
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.08);
}

.phone-modern {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(16, 61, 140, 0.22);
  border-radius: 12px;
  padding: 11px 12px;
  min-height: 56px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.phone-modern:focus-within {
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.14);
}

.phone-icon {
  flex: 0 0 auto;
}

.phone-input {
  width: 100%;
  outline: none;
  border: 0;
  font-size: 0.95rem;
  background: transparent;
  color: #1c2636;
}

.save-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-radius: 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.22);
}

.save-btn {
  min-width: 170px;
}

.confirm-card {
  border-radius: 16px;
}

.confirm-title {
  background: linear-gradient(120deg, #1e6fd0 0%, #3f8df0 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
}

.inputfile {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .account-header {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .account-page {
    padding: 8px 0 0;
  }

  .account-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .header-title {
    font-size: 1.2rem;
  }

  .section-card {
    padding: 14px;
  }

  .save-row {
    justify-content: stretch;
  }

  .save-btn {
    width: 100%;
  }
}
</style>
